import {
  createContext,
  FC,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useLazyQuery } from '@apollo/client';
import { Modal, Preloader, Separator } from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';

import { sanitizeHTML } from '../utils';
import s from './News.module.scss';
import { NEWS, INewsResponse } from './schema';

const ARTICLE_ID_KEY = 'last_article_id';
const saveLastArticleId = (id: string): void =>
  localStorage.setItem(ARTICLE_ID_KEY, id);
const getSavedArticleId = (): string | null =>
  localStorage.getItem(ARTICLE_ID_KEY);

interface INewsContext {
  showNews: VoidFunction;
}

export const NewsContext = createContext<INewsContext>({
  showNews: () => null,
});

export const News: FC = ({ children }) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { tokens },
  } = useContext(AuthContext);
  const [isNewsVisible, setIsNewsVisible] = useState(false);

  const [loadNews, { data: newsResponse, loading: newsLoading }] =
    useLazyQuery<INewsResponse>(NEWS, {
      fetchPolicy: 'network-only',
      onCompleted: (response) => {
        if (!response.news.data) return;

        const lastArticleId = response.news.data[0]?.id;
        const savedId = getSavedArticleId();

        if (!lastArticleId) return;

        if (lastArticleId !== savedId) {
          setIsNewsVisible(true);
        }

        saveLastArticleId(lastArticleId);
      },
    });

  const showNews = useCallback(() => {
    loadNews();
    setIsNewsVisible(true);
  }, []);

  useEffect(() => {
    if (!tokens?.access_token) {
      return setIsNewsVisible(false);
    }

    loadNews();
  }, [tokens?.access_token]);

  return (
    <NewsContext.Provider value={{ showNews }}>
      {isNewsVisible && (
        <Modal
          onClose={() => setIsNewsVisible(false)}
          className={s.container}
          dataTestId="news"
        >
          <div className={s.header}>{t('news')}</div>
          <Separator />
          <Preloader loading={newsLoading} className={s.preloader}>
            <div className={s.content}>
              {newsResponse?.news.data.map((article, index) => (
                <Fragment key={`article-${article.id}`}>
                  <div className={s.article}>
                    <div className={s.title}>
                      {article.title}
                      <span className={s.created}>
                        {dayjs(article.createdAt).format('DD MMM YYYY')}
                      </span>
                    </div>
                    {article.preview && (
                      <img
                        src={article.preview}
                        alt="article preview"
                        className={s.preview}
                      />
                    )}
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(article.content),
                      }}
                    />
                    {article.url && (
                      <a target="__blank" href={article.url}>
                        {t('more')}
                      </a>
                    )}
                  </div>
                  {index + 1 !== newsResponse.news.data.length && <Separator />}
                </Fragment>
              ))}
            </div>
          </Preloader>
        </Modal>
      )}
      {children}
    </NewsContext.Provider>
  );
};
